"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Expense = void 0;
const expense_discount_utils_1 = require("../../utils/expense.discount.utils");
const expense_utils_1 = require("../../utils/expense.utils");
const commentItem_model_1 = require("../comment/commentItem.model");
const billingPeriod_enum_1 = require("./billingPeriod.enum");
const expenseKind_enum_1 = require("./expenseKind.enum");
const expenseRequestType_enum_1 = require("./expenseRequestType.enum");
const expenseStatus_enum_1 = require("./expenseStatus.enum");
const subscriptionType_enum_1 = require("./subscriptionType.enum");
const vendorType_enum_1 = require("./vendorType.enum");
class Expense {
    constructor(data) {
        this.isReimbursement = false;
        this.comments = [];
        // @todo to improve typings
        this.renewalsExpenses = [];
        this.expenseTags = [];
        this.isCurrentUserExpense = (loggedInUserId) => (this.status === expenseStatus_enum_1.ExpenseStatusEnum.PURCHASE_PENDING || this.status === expenseStatus_enum_1.ExpenseStatusEnum.INVOICE_PENDING) && String(this.requestedBy._id) === loggedInUserId;
        this.isSubscription = () => this.kind === expenseKind_enum_1.ExpenseKindEnum.SUBSCRIPTION;
        this.isInitialSubscription = () => this.subscriptionType === subscriptionType_enum_1.SubscriptionTypeEnum.INITIAL_REQUEST;
        this.isAtLeastApproved = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.APPROVED || this.status === expenseStatus_enum_1.ExpenseStatusEnum.REVIEWED || this.status === expenseStatus_enum_1.ExpenseStatusEnum.PAID || this.status === expenseStatus_enum_1.ExpenseStatusEnum.DONE;
        this.isPaid = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.PAID;
        this.isProcessed = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.PAID || this.status === expenseStatus_enum_1.ExpenseStatusEnum.DONE || this.status === expenseStatus_enum_1.ExpenseStatusEnum.DECLINED;
        this.isSuccessfullyProcessed = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.PAID || this.status === expenseStatus_enum_1.ExpenseStatusEnum.DONE;
        this.isInReview = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.APPROVED;
        this.isReviewed = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.REVIEWED;
        this.isPending = () => this.status === expenseStatus_enum_1.ExpenseStatusEnum.PURCHASE_PENDING || this.status === expenseStatus_enum_1.ExpenseStatusEnum.DOCS_NEEDED || this.status === expenseStatus_enum_1.ExpenseStatusEnum.INVOICE_PENDING;
        this.cannotBeDeleted = () => this.isSubscription() && this.subscriptionType === subscriptionType_enum_1.SubscriptionTypeEnum.INITIAL_REQUEST && this.renewalsExpenses.length > 0;
        this.getRequestType = () => {
            if ([expenseKind_enum_1.ExpenseKindEnum.MILEAGE, expenseKind_enum_1.ExpenseKindEnum.HOSPITALITY, expenseKind_enum_1.ExpenseKindEnum.PER_DIEM].includes(this.kind)) {
                return expenseRequestType_enum_1.ExpenseRequestTypeEnum.REIMBURSEMENT;
            }
            return this.status === expenseStatus_enum_1.ExpenseStatusEnum.PURCHASE_PENDING ? expenseRequestType_enum_1.ExpenseRequestTypeEnum.PURCHASE : expenseRequestType_enum_1.ExpenseRequestTypeEnum.INVOICE;
        };
        this.hasBeenExportedTo = (exportFormat) => {
            const exportInfoProperty = (0, expense_utils_1.mapExportFormatToExportInfoProperty)(exportFormat);
            // If exportInfoProperty is undefined, then this.exportInformation does not store the information for a certain ExportFormatEnum (e.g. AUDIT_TRIAL info is not stored)
            if (!exportInfoProperty)
                return false;
            return this.exportInformation[exportInfoProperty];
        };
        this.extractSubscriptionDataFromExpense = () => ({
            name: this.name,
            link: this.link || "",
            description: this.description,
            datePurchased: new Date(this.datePurchased),
            dateCancellationDue: new Date(this.dateCancellationDue),
            currency: this.currency,
            subscriptionType: this.subscriptionType,
            renewalsExpenses: this.renewalsExpenses,
            billingPeriod: this.billingPeriod,
            monthlyPrice: this.monthlyPrice,
            quarterlyPrice: this.quarterlyPrice,
            yearlyPrice: this.yearlyPrice,
            parentSubscription: this.parentSubscription,
        });
        this.extractOneTimeExpenseDataFromExpense = () => ({
            description: this.description,
            currency: this.currency,
            isReimbursement: this.isReimbursement,
        });
        this.isGobdCompliantInvoice = () => !!this.gobdInvoiceComplianceDate;
        this.getActiveDiscount = () => (0, expense_discount_utils_1.getActiveExpenseDiscount)(this);
        this.getUsedOrActiveDiscount = () => (0, expense_discount_utils_1.getUsedOrActiveExpenseDiscount)(this);
        this.getSubscriptionBillingValue = () => {
            switch (this.billingPeriod) {
                case billingPeriod_enum_1.BillingPeriodEnum.MONTHLY:
                    return this.monthlyPrice;
                case billingPeriod_enum_1.BillingPeriodEnum.QUARTERLY:
                    return this.quarterlyPrice;
                case billingPeriod_enum_1.BillingPeriodEnum.YEARLY:
                    return this.yearlyPrice;
                default:
            }
            return this.yearlyPrice;
        };
        if (data) {
            this.deserialize(data);
        }
    }
    getCreditorName() {
        var _a;
        return (_a = this.getCreditor()) === null || _a === void 0 ? void 0 : _a.name;
    }
    getCreditor() {
        if (this.vendorType === vendorType_enum_1.VendorTypeEnum.VENDOR && this.vendor) {
            const vendorRef = this.vendor;
            return {
                _id: vendorRef._id,
                name: vendorRef.name,
                type: vendorType_enum_1.VendorTypeEnum.VENDOR,
                source: vendorRef,
                creditorNumber: vendorRef.creditorNumber,
                iban: vendorRef.iban,
                bic: vendorRef.bic,
            };
        }
        if (this.vendorType === vendorType_enum_1.VendorTypeEnum.USER && this.creditorUser) {
            const userCreditorRef = this.creditorUser;
            return {
                _id: userCreditorRef._id,
                name: [userCreditorRef.firstName, userCreditorRef.lastName].filter((n) => !!n).join(" "),
                type: vendorType_enum_1.VendorTypeEnum.USER,
                source: userCreditorRef,
                creditorNumber: userCreditorRef.creditorNumber,
                iban: userCreditorRef.iban,
                bic: userCreditorRef.bic,
            };
        }
        return undefined;
    }
    deserialize(input) {
        var _a, _b;
        Object.assign(this, input);
        this.id = input.id || input._id;
        this.splits = ((_a = input.splits) === null || _a === void 0 ? void 0 : _a.map((split) => split)) || [];
        this.comments = ((_b = input.comments) === null || _b === void 0 ? void 0 : _b.map((comment) => new commentItem_model_1.CommentItem(comment))) || [];
        this.invoices = (input === null || input === void 0 ? void 0 : input.invoices) || [];
        this.totalGrossPrice = (input === null || input === void 0 ? void 0 : input.totalGrossPrice) || 0;
        this.approvalProcesses = (input === null || input === void 0 ? void 0 : input.approvalProcesses) || [];
        return this;
    }
}
exports.Expense = Expense;
